import React from 'react';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HStep, HTip, HBold } from '../HandbookContent';

export default function ChapterLibrary() {
  return (
    <HChapter
      title="The Playbook Library"
      subtitle="Your central hub for browsing, managing, and organizing workflow playbooks."
    >
      <HMetaphor
        icon={<ArchiveBoxIcon className="w-5 h-5 text-lex-accent" />}
        title="Think of It as Your Firm's Filing Cabinet"
      >
        The Library is like a well-organized filing cabinet where every drawer holds a different
        workflow. System playbooks are the pre-printed templates from EdgeLex — reliable starting points.
        Your firm playbooks are the customized versions you've created. You can browse, filter,
        clone, and manage everything from one place.
      </HMetaphor>

      <HSection title="What You'll See">
        <HParagraph>
          When you open the Playbook Library, you'll see a list of playbooks organized into two groups:
        </HParagraph>
        <img
          src="/handbook/library-cards.svg"
          alt="Playbook Library panel showing header with filters for Practice Area, Lifecycle, and Source. Two playbook cards: a system playbook 'Family Law Dissolution' (v3, Active, System badge) with View and Clone buttons, and a firm variant 'Family Law Dissolution — Our Process' (v1, Draft) indented with a blue left border and Publish/Archive buttons"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-2">
          <li>
            <HBold>System playbooks</HBold> — Pre-built templates provided by EdgeLex. They appear with
            a "System" badge. You cannot edit these directly, but you can clone them to create your own version.
            Any firm variants (clones) are shown nested beneath their system parent.
          </li>
          <li>
            <HBold>Standalone playbooks</HBold> — Playbooks your firm built from scratch or imported
            from other sources. These appear in their own section.
          </li>
        </ul>
        <HParagraph>
          Each playbook card shows key information at a glance: its name, version number, lifecycle
          status (Draft, Active, etc.), practice areas, and how many statuses and transitions it contains.
        </HParagraph>
      </HSection>

      <HSection title="Filtering the Library">
        <HParagraph>
          Use the filter bar at the top to narrow down what you see:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-1">
          <li><HBold>Practice Area</HBold> — Show only playbooks for a specific area of law</li>
          <li><HBold>Lifecycle</HBold> — Filter by status: Draft, Active, Superseded, Inactive, or Archived</li>
          <li><HBold>Source</HBold> — Filter by how the playbook was created: Manual, AI-Generated, Court Rules, or Imported</li>
        </ul>
      </HSection>

      <HSection title="Viewing a Playbook">
        <HStep number={1} title="Click on any playbook card">
          This opens the detailed view with multiple tabs: Visual Editor, Structure, Guards &
          Validators, Actions, Usage, Analytics, Versions, and Webhooks.
        </HStep>
        <HStep number={2} title="Navigate the tabs">
          Each tab shows a different aspect of the playbook. Start with the Visual Editor to see
          the workflow diagram, or check the Structure tab for a text-based overview.
        </HStep>
        <HStep number={3} title="Click 'Back to Library' to return">
          The back button in the top-left corner takes you back to the library list.
        </HStep>
      </HSection>

      <HSection title="Quick Actions">
        <HParagraph>
          Each playbook card has action buttons for common operations:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-1">
          <li><HBold>Clone</HBold> — Create a copy of this playbook that you can customize</li>
          <li><HBold>Publish</HBold> — Make a draft playbook live (available for use with matters)</li>
          <li><HBold>Archive</HBold> — Remove a playbook from active use without deleting it</li>
        </ul>
      </HSection>

      <HTip>
        When a system playbook is updated by EdgeLex, you'll see an "Upstream changes available" badge
        on your firm's cloned version. Click it to review what changed and decide whether to incorporate
        the updates.
      </HTip>
    </HChapter>
  );
}
